/* expo-collection-tabs.js
 * HillCo Bee Expo - homepage collection tabs + native Shopify facets
 * ES5. Registered on `window` in the CAPTURE phase so it always runs before
 * Dawn's facets.js (which listens on document / on the custom elements).
 */
(function () {
  'use strict';

  var RESULTS_SECTION_FALLBACK = 'expo-collection-results';
  var AVAILABILITY = 'filter.v.availability=';
  var LOCK_CLASSES = [
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop'
  ];

  /* ------------------------------------------------------------------ utils */

  function closestEl(node, selector) {
    while (node && node.nodeType !== 1) { node = node.parentNode; }
    while (node) {
      if (node.nodeType === 1 && node.matches && node.matches(selector)) { return node; }
      node = node.parentNode || node.host;
    }
    return null;
  }

  function releaseScrollLock() {
    for (var i = 0; i < LOCK_CLASSES.length; i++) {
      document.body.classList.remove(LOCK_CLASSES[i]);
      document.documentElement.classList.remove(LOCK_CLASSES[i]);
    }
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
  }

  /* -------------------------------------------------------------- component */

  function ExpoTabs(root) {
    this.root = root;
    this.resultsSection = root.getAttribute('data-results-section') || RESULTS_SECTION_FALLBACK;
    this.results = root.querySelector('[data-exp-results]');
    this.loader = root.querySelector('[data-exp-loading]');
    this.nav = root.querySelector('.exp-tabs__nav');
    this.dotsWrap = root.querySelector('[data-exp-dots]');

    /* Two section shapes are supported:
       1. the results are rendered server side  -> [data-exp-results] exists
       2. the section ships an empty shell      -> [data-exp-target] is filled here */
    this.target = root.querySelector('[data-exp-target]');
    if (!this.target && this.results) { this.target = this.results.parentNode; }

    var activeTab = root.querySelector('[data-exp-tab].is-active') ||
      root.querySelector('[data-exp-tab]:not([data-exp-redirect])') ||
      root.querySelector('[data-exp-tab]');
    this.collectionUrl =
      (this.results && this.results.getAttribute('data-collection-url')) ||
      (activeTab && activeTab.getAttribute('data-exp-url')) || '';

    this.query = '';
    this.pendingForm = null;   /* price / mobile-drawer changes held until commit */
    this.busy = false;

    /* tag / sold out badges, described once by the section */
    this.badges = [];
    var badgeRaw = root.getAttribute('data-exp-badges');
    if (badgeRaw) {
      try { this.badges = JSON.parse(badgeRaw) || []; } catch (e) { this.badges = []; }
    }

    /* sold out last: compose each page from two server rendered streams */
    this.soldOutLast = root.getAttribute('data-sold-out-last') === 'true';
    this.soldOutCache = null;

    this.bind();

    if (this.results) { this.afterRender(); }
    else { this.load('', 1, false); }   /* empty shell - fetch the first collection */
  }

  /* ---------------------------------------------------------------- scoping */

  ExpoTabs.prototype.owns = function (node) {
    return !!(node && this.root.contains(node.nodeType === 1 ? node : node.parentNode));
  };

  /* A node that belongs to the facet / sort UI (NOT a product form). */
  ExpoTabs.prototype.isFacetNode = function (node) {
    if (!this.owns(node)) { return false; }
    return !!closestEl(
      node,
      'facet-filters-form, .facets, .facets__form, .mobile-facets, ' +
      '.mobile-facets__wrapper, .active-facets, .facet-filters, price-range, ' +
      'facet-remove, .facets-container'
    );
  };

  ExpoTabs.prototype.isPriceNode = function (node) {
    if (!this.owns(node)) { return false; }
    if (closestEl(node, 'price-range, .facets__price, .price-range')) { return true; }
    var name = node && node.name;
    return !!(name && name.indexOf('filter.v.price') === 0);
  };

  ExpoTabs.prototype.inDrawer = function (node) {
    return !!closestEl(node, '.mobile-facets__wrapper, .mobile-facets');
  };

  /* ------------------------------------------------------------------ query */

  ExpoTabs.prototype.queryFromForm = function (form) {
    var parts = [];
    var seenSort = false;
    var data = new FormData(form);
    var pairs = [];
    data.forEach(function (value, key) { pairs.push([key, value]); });

    for (var i = 0; i < pairs.length; i++) {
      var key = pairs[i][0];
      var value = pairs[i][1];
      if (key === 'section_id' || key === 'page') { continue; }
      if (value === '' || value === null) { continue; }
      if (key === 'sort_by') { seenSort = true; }
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }

    /* the drawer form has no sort select - keep whatever is applied on desktop */
    if (!seenSort) {
      var sortSelect = this.root.querySelector('select[name="sort_by"]');
      if (sortSelect && sortSelect.value) {
        parts.push('sort_by=' + encodeURIComponent(sortSelect.value));
      }
    }
    return parts.join('&');
  };

  ExpoTabs.prototype.queryFromHref = function (href) {
    var q = String(href).split(String.fromCharCode(63))[1] || '';
    return q.split('&').filter(function (part) {
      return part && part.indexOf('section_id=') !== 0 && part.indexOf('page=') !== 0;
    }).join('&');
  };

  /* ------------------------------------------------------------------- load */

  /* ------------------------------------------------------------ fetching */

  ExpoTabs.prototype.sectionUrl = function (query, page) {
    return this.collectionUrl +
      String.fromCharCode(63) + 'section_id=' + this.resultsSection +
      (query ? '&' + query : '') +
      (page && page > 1 ? '&page=' + page : '');
  };

  ExpoTabs.prototype.fetchDoc = function (query, page) {
    return fetch(this.sectionUrl(query, page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) { return response.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  };

  ExpoTabs.prototype.hasAvailabilityFilter = function (query) {
    return String(query || '').indexOf(AVAILABILITY) > -1;
  };

  ExpoTabs.prototype.withAvailability = function (query, value) {
    var parts = [];
    var raw = query ? query.split('&') : [];
    for (var i = 0; i < raw.length; i++) {
      if (raw[i] && raw[i].indexOf(AVAILABILITY) !== 0) { parts.push(raw[i]); }
    }
    parts.push(AVAILABILITY + value);
    return parts.join('&');
  };

  /* Put a freshly fetched results node into the page. */
  ExpoTabs.prototype.mount = function (fresh) {
    if (!fresh) { return false; }
    if (this.results && this.results.parentNode) {
      this.results.parentNode.replaceChild(fresh, this.results);
    } else if (this.target) {
      this.target.innerHTML = '';
      this.target.appendChild(fresh);
    } else {
      return false;
    }
    this.results = fresh;
    this.collectionUrl = fresh.getAttribute('data-collection-url') || this.collectionUrl;
    return true;
  };

  ExpoTabs.prototype.finish = function (scroll) {
    this.busy = false;
    if (this.loader) { this.loader.hidden = true; }
    releaseScrollLock();
    this.afterRender();
    if (scroll) { this.scrollToTop(); }
  };

  /* --------------------------------------------------------------- load */

  ExpoTabs.prototype.load = function (query, page, scroll) {
    if (this.busy) { return; }
    this.query = typeof query === 'string' ? query : this.query;
    this.pendingForm = null;

    /* The shopper's own availability filter wins: composing on top of it
       would contradict what they picked. */
    if (this.soldOutLast && !this.hasAvailabilityFilter(this.query)) {
      this.loadComposed(page, scroll);
    } else {
      this.loadPlain(page, scroll);
    }
  };

  ExpoTabs.prototype.loadPlain = function (page, scroll) {
    var self = this;
    this.busy = true;
    if (this.loader) { this.loader.hidden = false; }

    this.fetchDoc(this.query, page)
      .then(function (doc) { self.mount(doc.querySelector('[data-exp-results]')); })
      .catch(function () { /* leave the previous grid in place */ })
      .then(function () { self.finish(scroll); });
  };

  /* Every truly sold out card, across all of that stream's pages, cached per
     collection + filter combination.

     The availability=0 stream also returns products that merely have SOME
     variant sold out, so it is filtered down to data-available="false", which
     is Liquid's product.available and only false when nothing is buyable.
     That is what keeps the two streams from overlapping. */
  ExpoTabs.prototype.soldOutItems = function (doc) {
    var nodes = doc.querySelectorAll('.exp-grid__item[data-available="false"]');
    var out = [];
    for (var i = 0; i < nodes.length; i++) { out.push(nodes[i]); }
    return out;
  };

  ExpoTabs.prototype.appendSoldOutPage = function (query, page) {
    var self = this;
    return function (collected) {
      return self.fetchDoc(query, page).then(function (doc) {
        return collected.concat(self.soldOutItems(doc));
      });
    };
  };

  ExpoTabs.prototype.loadSoldOutStream = function () {
    var self = this;
    var key = this.collectionUrl + '|' + (this.query || '');

    if (this.soldOutCache && this.soldOutCache.key === key) {
      return Promise.resolve(this.soldOutCache.items);
    }

    var query = this.withAvailability(this.query, 0);

    return this.fetchDoc(query, 1)
      .then(function (doc) {
        var results = doc.querySelector('[data-exp-results]');
        var pages = results ? parseInt(results.getAttribute('data-pages'), 10) || 1 : 1;
        var chain = Promise.resolve(self.soldOutItems(doc));
        for (var page = 2; page <= pages; page++) {
          chain = chain.then(self.appendSoldOutPage(query, page));
        }
        return chain;
      })
      .then(function (items) {
        self.soldOutCache = { key: key, items: items };
        return items;
      })
      .catch(function () { return []; });
  };

  /* Compose one page: in stock items first, in their own order, then the sold
     out tail. Totals are unchanged, so the unfiltered response's pager and
     product count are already correct and are reused as they are. */
  ExpoTabs.prototype.loadComposed = function (page, scroll) {
    var self = this;
    this.busy = true;
    if (this.loader) { this.loader.hidden = false; }

    var target = page && page > 1 ? page : 1;

    Promise.all([
      this.fetchDoc(this.query, target),                          /* chrome, pager, count */
      this.fetchDoc(this.withAvailability(this.query, 1), target),/* in stock, same page */
      this.loadSoldOutStream()                                    /* sold out, cached */
    ])
      .then(function (parts) {
        var baseDoc = parts[0];
        var inDoc = parts[1];
        var soldItems = parts[2];

        var fresh = baseDoc.querySelector('[data-exp-results]');
        if (!fresh) { return; }

        var grid = fresh.querySelector('.exp-grid');
        if (!grid) { self.mount(fresh); return; }

        var pageSize = parseInt(fresh.getAttribute('data-page-size'), 10) || 20;
        var inResults = inDoc.querySelector('[data-exp-results]');
        var inTotal = inResults ? parseInt(inResults.getAttribute('data-total'), 10) || 0 : 0;

        var from = (target - 1) * pageSize;
        var takeInStock = Math.min(Math.max(inTotal - from, 0), pageSize);

        var chosen = [];
        var i;

        if (takeInStock > 0) {
          var inItems = inDoc.querySelectorAll('.exp-grid__item');
          for (i = 0; i < inItems.length && chosen.length < takeInStock; i++) {
            chosen.push(inItems[i]);
          }
        }

        var soldFrom = Math.max(0, from - inTotal);
        for (i = soldFrom; i < soldItems.length && chosen.length < pageSize; i++) {
          chosen.push(soldItems[i]);
        }

        if (!self.mount(fresh)) { return; }

        var liveGrid = self.results.querySelector('.exp-grid');
        if (!liveGrid) { return; }
        liveGrid.innerHTML = '';
        for (i = 0; i < chosen.length; i++) {
          liveGrid.appendChild(document.importNode(chosen[i], true));
        }
      })
      .catch(function () { /* leave the previous grid in place */ })
      .then(function () { self.finish(scroll); });
  };

  ExpoTabs.prototype.scrollToTop = function () {
    var top = this.root.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: top, behavior: 'smooth' });
  };

  /* ----------------------------------------------------------- after render */

  ExpoTabs.prototype.afterRender = function () {
    this.placeLogoTile();
    this.paintBadges();
    this.markActiveFilters();
    this.syncDots();
  };

  /* Put each configured badge on the cards it belongs to.

     The section hands over the badge list as JSON; the grid items carry
     data-tags and data-available. Everything about how a badge looks, and
     where on the image it sits, is already in the section CSS, so the only
     job here is to insert a span and give it the block's class.

     A tag badge set to hide when sold out is skipped for unavailable
     products, which is what lets a Sold out badge at the same position take
     its place. */
  ExpoTabs.prototype.paintBadges = function () {
    if (!this.results || !this.badges.length) { return; }

    var items = this.results.querySelectorAll('.exp-grid__item');

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.className.indexOf('exp-grid__logo') > -1) { continue; }

      var host = item.querySelector('.img_wraper');
      if (!host) { continue; }

      /* clear anything a previous pass left behind */
      var old = host.querySelectorAll('.exp-tag-badge');
      for (var o = 0; o < old.length; o++) { old[o].parentNode.removeChild(old[o]); }

      var soldOut = item.getAttribute('data-available') === 'false';
      var onSale = item.getAttribute('data-on-sale') === 'true';
      var tags = '|' + (item.getAttribute('data-tags') || '') + '|';

      for (var b = 0; b < this.badges.length; b++) {
        var badge = this.badges[b];
        var show = false;

        if (badge.match === 'sold_out') {
          show = soldOut;
        } else if (badge.match === 'on_sale') {
          show = onSale;
          if (show && badge.hideSoldOut && soldOut) { show = false; }
        } else if (badge.tag) {
          show = tags.indexOf('|' + badge.tag + '|') > -1;
          if (show && badge.hideSoldOut && soldOut) { show = false; }
        }

        if (!show) { continue; }

        var el = document.createElement('span');
        el.className = 'exp-tag-badge ' + badge.cls;
        el.textContent = badge.label || '';
        host.appendChild(el);
      }
    }
  };

  /* The collection logo ships above the grid on older markup. Move it into the
     grid as the first cell, where the CSS makes it span two card slots.
     Page 1 only. A no-op when the section already renders it as a cell. */
  ExpoTabs.prototype.placeLogoTile = function () {
    if (!this.results) { return; }
    var grid = this.results.querySelector('.exp-grid');
    var logo = this.results.querySelector('.exp-results__logo');
    if (!grid || !logo) { return; }

    var page = parseInt(this.results.getAttribute('data-page'), 10) || 1;
    if (page !== 1) {
      if (logo.parentNode) { logo.parentNode.removeChild(logo); }
      return;
    }
    if (grid.querySelector('.exp-grid__logo')) { return; }

    var cell = document.createElement('li');
    cell.className = 'exp-grid__item exp-grid__logo';
    cell.appendChild(logo);
    grid.insertBefore(cell, grid.firstChild);
  };

  ExpoTabs.prototype.markActiveFilters = function () {
    if (!this.results) { return; }
    var active = this.results.querySelector('.active-facets .active-facets__button, .active-facets facet-remove');
    this.results.classList.toggle('exp-has-filters', !!active);
  };

  /* ---------------------------------------------------------------- drawer */

  ExpoTabs.prototype.closeDrawer = function () {
    var wrapper = this.root.querySelector('.mobile-facets__wrapper');
    if (wrapper) {
      var details = wrapper.querySelector('details');
      if (details) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        var summary = details.querySelector('summary');
        if (summary) { summary.setAttribute('aria-expanded', 'false'); }
      }
      wrapper.classList.remove('menu-opening');
      if (typeof wrapper.close === 'function') {
        try { wrapper.close(); } catch (e) { /* noop */ }
      }
    }
    releaseScrollLock();
  };

  ExpoTabs.prototype.drawerIsOpen = function () {
    var wrapper = this.root.querySelector('.mobile-facets__wrapper details');
    return !!(wrapper && wrapper.hasAttribute('open'));
  };

  /* Clear inputs locally instead of navigating (drawer stays open). */
  ExpoTabs.prototype.clearInputs = function (scope) {
    var inputs = scope.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.type === 'checkbox' || input.type === 'radio') { input.checked = false; }
      else if (input.name && input.name.indexOf('filter.') === 0) { input.value = ''; }
    }
  };

  /* ------------------------------------------------------------ commit hold */

  ExpoTabs.prototype.commitPending = function () {
    if (!this.pendingForm) { return false; }
    var form = this.pendingForm;
    this.pendingForm = null;
    this.load(this.queryFromForm(form), 1, false);
    return true;
  };

  /* ------------------------------------------------------------------ tabs */

  ExpoTabs.prototype.selectTab = function (button) {
    var buttons = this.root.querySelectorAll('[data-exp-tab]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].hasAttribute('data-exp-redirect')) { continue; }
      var isActive = buttons[i] === button;
      buttons[i].classList.toggle('is-active', isActive);
      buttons[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
    this.collectionUrl = button.getAttribute('data-exp-url') || this.collectionUrl;
    this.query = '';
    this.soldOutCache = null;
    this.load('', 1, false);

    if (this.nav && button.scrollIntoView) {
      try { button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' }); }
      catch (e) { /* noop */ }
    }
  };

  ExpoTabs.prototype.syncDots = function () {
    if (!this.dotsWrap || !this.nav) { return; }
    var dots = this.dotsWrap.querySelectorAll('[data-exp-dot]');
    if (!dots.length) { return; }

    var max = this.nav.scrollWidth - this.nav.clientWidth;
    var index;
    if (max <= 1) {
      var active = this.root.querySelector('[data-exp-tab].is-active');
      index = active ? parseInt(active.getAttribute('data-exp-tab'), 10) : 1;
    } else {
      var ratio = this.nav.scrollLeft / max;
      index = Math.round(ratio * (dots.length - 1)) + 1;
    }
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', parseInt(dots[i].getAttribute('data-exp-dot'), 10) === index);
    }
  };

  /* --------------------------------------------------------------- bindings */

  ExpoTabs.prototype.bind = function () {
    var self = this;

    /* --- CHANGE: checkboxes apply at once; price + drawer are held --------- */
    window.addEventListener('change', function (event) {
      var target = event.target;
      if (!self.owns(target)) { return; }

      /* sort select */
      if (target.name === 'sort_by') {
        event.stopPropagation();
        var sortForm = closestEl(target, 'form');
        if (!sortForm) { return; }
        /* the drawer has its own sort select - hold it until Apply */
        if (self.inDrawer(target)) { self.pendingForm = sortForm; return; }
        self.load(self.queryFromForm(sortForm), 1, false);
        return;
      }

      if (!self.isFacetNode(target)) { return; }
      event.stopPropagation();

      var form = closestEl(target, 'form');
      if (!form) { return; }

      /* mobile drawer: hold everything until Apply */
      if (self.inDrawer(target)) { self.pendingForm = form; return; }

      /* desktop price: hold until Enter / dropdown close / outside click */
      if (self.isPriceNode(target)) { self.pendingForm = form; return; }

      self.load(self.queryFromForm(form), 1, false);
    }, true);

    /* --- INPUT: never let Dawn's facets.js see it (getSections crash) ------ */
    window.addEventListener('input', function (event) {
      if (self.isFacetNode(event.target)) { event.stopPropagation(); }
    }, true);

    /* --- KEYDOWN: Enter inside a price field commits ----------------------- */
    window.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.keyCode !== 13) { return; }
      if (!self.isFacetNode(event.target)) { return; }
      event.preventDefault();
      event.stopPropagation();
      var form = closestEl(event.target, 'form');
      if (!form) { return; }
      if (self.inDrawer(event.target)) { self.pendingForm = form; return; }
      self.pendingForm = form;
      self.commitPending();
    }, true);

    /* --- SUBMIT: only facet forms; product forms must pass through --------- */
    window.addEventListener('submit', function (event) {
      if (!self.isFacetNode(event.target)) { return; }
      event.preventDefault();
      event.stopPropagation();
      var form = event.target;
      if (self.inDrawer(form)) { self.closeDrawer(); }
      self.load(self.queryFromForm(form), 1, false);
    }, true);

    /* --- TOGGLE: closing a desktop dropdown commits held price ------------- */
    window.addEventListener('toggle', function (event) {
      var details = event.target;
      if (!self.owns(details) || !details.matches || !details.matches('details')) { return; }
      if (self.inDrawer(details)) { return; }          /* drawer never auto-applies */
      if (!details.hasAttribute('open')) { self.commitPending(); }
    }, true);

    /* --- CLICK ------------------------------------------------------------- */
    window.addEventListener('click', function (event) {
      var target = event.target;

      /* tabs */
      var tab = closestEl(target, '[data-exp-tab]');
      if (tab && self.owns(tab)) {
        /* A tab set to "Go to collection page" is a real link: let the browser
           follow it so new tab and middle click keep working. */
        var redirect = tab.getAttribute('data-exp-redirect');
        if (redirect) {
          if (tab.tagName !== 'A') {
            event.preventDefault();
            window.location.href = redirect;
          }
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        self.selectTab(tab);
        return;
      }

      /* pagination */
      var pageLink = closestEl(target, '[data-exp-page]');
      if (pageLink && self.owns(pageLink)) {
        event.preventDefault();
        event.stopPropagation();
        self.load(self.query, parseInt(pageLink.getAttribute('data-exp-page'), 10), true);
        return;
      }

      if (!self.owns(target)) {
        /* click outside the section commits a held desktop price */
        self.commitPending();
        return;
      }

      /* Apply */
      var apply = closestEl(target, '.mobile-facets__submit, .mobile-facets__footer .button--primary, [data-exp-apply]');
      if (apply) {
        event.preventDefault();
        event.stopPropagation();
        var applyForm = self.pendingForm || closestEl(apply, 'form');
        self.pendingForm = null;
        self.closeDrawer();
        if (applyForm) { self.load(self.queryFromForm(applyForm), 1, false); }
        return;
      }

      /* Close (X) - discard held changes, close only on this click */
      var close = closestEl(target, '.mobile-facets__close, [data-exp-close]');
      if (close) {
        event.preventDefault();
        event.stopPropagation();
        self.pendingForm = null;
        self.closeDrawer();
        return;
      }

      /* clear / remove-all links */
      var clear = closestEl(target, '.mobile-facets__clear, .active-facets__button-remove, .active-facets a, facet-remove a');
      if (clear) {
        event.preventDefault();
        event.stopPropagation();
        if (self.inDrawer(clear)) {
          var scope = closestEl(clear, '.mobile-facets__details') || closestEl(clear, 'form');
          if (scope) { self.clearInputs(scope); }
          self.pendingForm = closestEl(clear, 'form');
          return;                                        /* drawer stays open */
        }
        self.load(self.queryFromHref(clear.getAttribute('href') || ''), 1, false);
        return;
      }

      /* clicking elsewhere inside the section, outside an open price dropdown */
      if (self.pendingForm && !self.inDrawer(target) && !self.isPriceNode(target) &&
          !closestEl(target, 'price-range, .facets__price')) {
        self.commitPending();
      }
    }, true);

    /* --- drawer opened: make sure no stale scroll lock survives ------------ */
    window.addEventListener('keyup', function (event) {
      if (event.key === 'Escape' && self.drawerIsOpen()) {
        self.pendingForm = null;
        self.closeDrawer();
      }
    }, true);

    /* --- tab strip scroll -> dots ----------------------------------------- */
    if (this.nav) {
      var raf;
      this.nav.addEventListener('scroll', function () {
        if (raf) { return; }
        raf = window.requestAnimationFrame(function () { raf = null; self.syncDots(); });
      }, { passive: true });
    }
    window.addEventListener('resize', function () { self.syncDots(); });
  };

  /* ------------------------------------------------------------------ init */

  function init() {
    var roots = document.querySelectorAll('[data-exp-tabs]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].expoTabs) { continue; }
      roots[i].expoTabs = new ExpoTabs(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();